import React, { useEffect, useState, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-with-locales-es6";

import downloadFileIcon from "../../assets/images/file-arrow-down-solid.svg";

import "./styles.css";

import { fileDownload, getQueryStringParams, isElectric } from "../../utils/utils";

import {
  downloadRentalAgreementDocument,
  getReservationRADetails,
} from "./api";
import { GroupsImagesContext } from "../../domain-repository/GroupsImagesRepository";

import Loading, { LoadingIcon } from "../../components/Loading";
import withLang from "../../withLang";
import { DomainContext } from "../../domain-repository/DomainRepository";
import useAxiosFetch from "../../utils/useAxiosFetch";
import TrackingPage from "../../components/TrackingPage";

function ReviewRentalAgreementPage({ location, history }) {
  const { groupsDetails, localizationAssets } = useContext(DomainContext);
  const { groupsConfigurations } = useContext(GroupsImagesContext);

  const [reservationNumber, setReservationNumber] = useState(null);
  const [pickupStation, setPickupStation] = useState(null);
  const [pickupDate, setPickupDate] = useState(null);

  const [raDetails, setRaDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleNotFound = useCallback(
    (reservationNumber) => {
      return history.push(
        `/input-reservation-ref-error?resReference=${reservationNumber}`
      );
    },
    [history]
  );

  useEffect(() => {
    if (location) {
      const queryStringValues = getQueryStringParams(
        location.search ? location.search : null
      );
      if (
        !queryStringValues ||
        !queryStringValues.number ||
        !queryStringValues.pickupStation ||
        !queryStringValues.pickupDate
      ) {
        handleNotFound(queryStringValues?.number || "");
      } else {
        setReservationNumber(queryStringValues.number);
        setPickupStation(queryStringValues.pickupStation);
        setPickupDate(queryStringValues.pickupDate);
      }
    }
  }, [location, handleNotFound]);

  useEffect(() => {
    if (reservationNumber && pickupDate && pickupStation) {
      setIsLoading(true);
      getReservationRADetails(reservationNumber, pickupDate, pickupStation)
        .then((res) => {
          if (!res) {
            handleNotFound(reservationNumber);
          } else {
            setRaDetails(res);
            setIsLoading(false);
          }
        })
        .catch(() => {
          handleNotFound(reservationNumber);
        });
    }
  }, [reservationNumber, pickupStation, pickupDate]);

  const imgUrl = raDetails
    ? groupsConfigurations[raDetails.groupId]?.imageUrl
    : "";

  const groupInfo = raDetails
    ? groupsDetails.find((g) => g.id === raDetails.groupId) || {}
    : {};

  return isLoading || !raDetails ? (
    <Loading active={true} />
  ) : (
    <ReviewRentalAgreementPageView
      raDetails={raDetails}
      imgUrl={imgUrl}
      groupInfo={groupInfo}
      localizationAssets={localizationAssets}
      groupsConfigurations={groupsConfigurations}
    />
  );
}

export default withLang(TrackingPage(ReviewRentalAgreementPage, 85));

function ReviewRentalAgreementPageView({
  raDetails,
  imgUrl,
  groupInfo,
  groupsConfigurations,
  localizationAssets,
}) {
  const { t, i18n } = useTranslation();

  const groupId = raDetails.groupId;

  return (
    <div className="ra-page">
      <div className="ra__banner">
        <div className="container ra__banner-content">
          <img src={require(`../../assets/images/logoWhite.svg`)} />
          <div className="ra__banner-greet">
            <div className="bold">
              <span className="primary-color">{t("hi")}, </span>
              {raDetails.driverName}!
            </div>
            <div
              style={{ width: "60%", margin: "0 auto" }}
              className="primary-color"
            >
              {t("mobile_raGreetMessage")}
            </div>
          </div>
        </div>
      </div>
      <div className="container">
        <div className="ra__section-margin">
          <div className="ra__section-title">
            {t("mobile_raNumber")} {raDetails.raNumber}
          </div>
          <div>
            <span className="bold">{t("mobile_raDays")}</span> {raDetails.days}{" "}
            {t("day")}
          </div>
          <div className="ra__stations">
            <StationDetail
              title={t("return")}
              stationName={t(`station${raDetails.pickupStationId}Name`)}
              date={raDetails.pickupDate}
              language={i18n.language}
            />
            <StationDetail
              title={t("dropoff")}
              stationName={t(
                `station${
                  raDetails.dropoffStationId || raDetails.resDropoffStationId
                }Name`
              )}
              date={raDetails.dropoffDate}
              language={i18n.language}
            />
          </div>
        </div>
        <VehicleDetail
          t={t}
          groupId={groupId}
          plateNumber={raDetails.plateNumber}
          groupInfo={groupInfo}
          imgUrl={imgUrl}
          groupsConfigurations={groupsConfigurations}
          localizationAssets={localizationAssets}
        />
        <ExtrasSection t={t} extras={raDetails.extras} />
        <DocumentsSection t={t} raNumber={raDetails.raNumber} />
        <div className="primary-color bold ra__section-margin ta--center fs-big">
          {t("mobile_raThankYou")}
        </div>
      </div>
      <Footer t={t} />
    </div>
  );
}

function VehicleDetail({
  t,
  groupId,
  groupInfo,
  imgUrl,
  groupsConfigurations,
  plateNumber,
  localizationAssets,
}) {
  return (
    <>
      <div className="ra__section-margin">
        <div className="ra__section-title">{t("mobile_raDetails")}</div>
        <div className="ra__platenumber">
          <span className="bold">{t("mobile_raPlateNumber")}</span>
          <span>{plateNumber}</span>
        </div>
      </div>
      <div className="ra__vehicle">
        <div className="ra__vehicle-img">
          <img src={imgUrl} alt={groupId} />
        </div>
        <div className="ra__vehicle-type">{groupInfo.type}</div>
        <div className="ra__vehicle-properties">
          <div>
            <span className="bold">{isElectric(groupInfo.fuel) ? t('range') : t("consumption")}:</span>
            {groupsConfigurations[groupId]?.consumption}
          </div>
          <div>
            <span className="bold">{t("seats")}:</span>
            {groupInfo.seats}
          </div>
          <div>
            <span className="bold">{t("transmisson")}:</span>
            {t("vehicles-" + groupInfo.transmission, "")}
          </div>
          <div>
            <span className="bold">{t("doors")}:</span>
            {groupInfo.doors}
          </div>
          <div>
            <span className="bold">{t("bags")}:</span>
            {groupInfo.bags}
          </div>
          <div>
            <span className="bold">{t("handbags")}:</span>
            {groupInfo.handbags}
          </div>
        </div>
      </div>
    </>
  );
}

function StationDetail({ title, date, stationName, language }) {
  return (
    <div className="ra__stations-item">
      <div className="ra__stations-item-img">
        <img src={require("../../assets/images/ico-pin-map.png")} alt="" />
      </div>
      <div className="ra__stations-item-content">
        <div className="ra__stations-item-name">{title}</div>
        <div className="bold">{stationName}</div>
        <div className="bold">
          {moment(date, "YYYY-MM-DD HH:mm")
            .locale(language)
            ?.format("ddd D MMM / HH:mm")}
        </div>
      </div>
    </div>
  );
}

function Footer({ t }) {
  return (
    <div className="ra__section-margin ra__footer">
      <div className="container">
        <div className="ra__footer-contacts">
          <div>
            <div className="ra__footer-text-1">{t("mobile_raFooterHelp")}</div>
            <a className="ra__footer-text-2" href="tel:+351210100200">
              +351 210 100 200 (ext2)
            </a>
            <div className="ra__footer-text-3">
              {t("mobile_raFooterSchedule")}
            </div>
          </div>
          <div>
            <a
              className="ra__footer-text-2"
              href="mailto:operationsbackoffice@guerin.com.pt"
            >
              operationsbackoffice@guerin.com.pt
            </a>
          </div>
        </div>
        <div className="ra__footer-media">
          <a
            className="ra__footer-link"
            href="https://www.guerin.pt"
            target="_blank"
          >
            www.guerin.pt
          </a>
          <div className="ra__footer-divider" />
          <div className="ra__footer-socials">
            <a
              className="ra__footer-socials-item"
              href="https://pt-pt.facebook.com/guerinportugal/"
              target="_blank"
            >
              f
            </a>
            <a
              className="ra__footer-socials-item"
              href="https://pt.linkedin.com/company/guerin-enterprise"
              target="_blank"
            >
              in
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExtrasSection({ t, extras }) {
  return (
    <div className="ra__section-margin ra__extras">
      <div className="ra__extras-title">{t("mobile_raExtras")}</div>
      <div className="ra__extras-items">
        {extras.map((extra) => (
          <div className="ra__extras-item">{t(extra.id + "Name")}</div>
        ))}
      </div>
    </div>
  );
}

function DocumentsSection({ t, raNumber }) {
  const { data: documents, loading } = useAxiosFetch(
    `/rental-agreements/${raNumber}/documents`
  );

  return (
    <div className="ra__section-margin">
      <div className="ra__section-title">{t("mobile_raDocuments")}</div>
      {loading && <LoadingIcon />}
      <div className="grid gap-4">
        {documents?.map((doc) => (
          <DocumentsSectionItem
            key={doc.id}
            t={t}
            doc={doc}
            raNumber={raNumber}
          />
        ))}
      </div>
    </div>
  );
}

function DocumentsSectionItem({ t, doc, raNumber }) {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadClick = () => {
    setIsDownloading(true);
    downloadRentalAgreementDocument(raNumber, doc.fileName)
      .then((buffer) => {
        fileDownload(
          new Uint8Array(buffer.data),
          doc.fileName,
          "application/pdf"
        );
      })
      .catch((error) => {
        alert("File not found");
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  return (
    <div className="flex gap-4 items-center">
      {isDownloading ? (
        <LoadingIcon />
      ) : (
        <img
          className="pointer"
          style={{ width: '20px' }}
          onClick={() => {
            if (!isDownloading) handleDownloadClick();
          }}
          src={downloadFileIcon}
          alt=""
        />
      )}
      <div>{doc.description}</div>
    </div>
  );
}
